import { useEffect, useRef } from 'react';
import {
  commands as defaultCommands,
  type Command,
  type CommandContext,
  type CommandScope,
} from '../lib/keymap';

/**
 * Scope-aware keyboard dispatcher.
 *
 * Every component that can own keys registers a scope plus the slice of the
 * command context it can service. A keystroke resolves in the innermost active
 * scope first (composer beats reading beats list beats global) and stops there.
 */

const SCOPE_PRECEDENCE: CommandScope[] = ['composer', 'reading', 'list', 'global'];

const CHORD_TIMEOUT_MS = 1200;

type Registration = {
  scope: CommandScope;
  ref: { current: Partial<CommandContext> };
  enabled: boolean;
  order: number;
};

const registrations: Registration[] = [];
let orderCounter = 0;
let listening = false;

let pendingChord: string | null = null;
let pendingTimer: ReturnType<typeof setTimeout> | null = null;

/* ------------------------------ key encoding ------------------------------ */

const CODE_TO_CHAR: Record<string, string> = {
  Comma: ',',
  Period: '.',
  Slash: '/',
  Backslash: '\\',
  Semicolon: ';',
  Quote: "'",
  BracketLeft: '[',
  BracketRight: ']',
  Minus: '-',
  Equal: '=',
  Backquote: '`',
};

function baseFromCode(code: string): string | null {
  if (/^Key[A-Z]$/.test(code)) return code.slice(3).toLowerCase();
  if (/^Digit[0-9]$/.test(code)) return code.slice(5);
  if (/^Numpad[0-9]$/.test(code)) return code.slice(6);
  return CODE_TO_CHAR[code] ?? null;
}

const APPLE_LIKE =
  typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent);

function withModifiers(event: KeyboardEvent, base: string, includeShift: boolean): string {
  const parts: string[] = [];
  if (event.metaKey || (!APPLE_LIKE && event.ctrlKey)) parts.push('Mod');
  // On Apple hardware Ctrl is a separate modifier from the Mod (⌘) key.
  if (APPLE_LIKE && event.ctrlKey) parts.push('Ctrl');
  if (event.altKey) parts.push('Alt');
  if (includeShift && event.shiftKey) parts.push('Shift');
  parts.push(base);
  return parts.join('+');
}

const NAMED_KEYS = new Set([
  'Enter',
  'Escape',
  'Tab',
  'Delete',
  'Backspace',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
  'PageUp',
  'PageDown',
]);

/**
 * A single keystroke can legitimately be spelled several ways: `Shift+3` and
 * `#` are the same physical event, and on macOS Alt mangles `event.key`
 * entirely. We produce every canonical spelling and let the keymap match any.
 */
export function candidateKeys(event: KeyboardEvent): string[] {
  const out: string[] = [];
  const push = (value: string) => {
    if (!out.includes(value)) out.push(value);
  };

  const key = event.key;

  if (key === ' ' || event.code === 'Space') {
    push(withModifiers(event, 'Space', true));
  } else if (NAMED_KEYS.has(key)) {
    push(withModifiers(event, key, true));
  } else if (key.length === 1) {
    const isLetter = /[a-z]/i.test(key);
    if (isLetter) {
      push(withModifiers(event, key.toLowerCase(), true));
    } else {
      // Shift is inherent to producing '#' or '!', so offer it both ways.
      push(withModifiers(event, key, false));
      push(withModifiers(event, key, true));
    }
  }

  const fromCode = baseFromCode(event.code);
  if (fromCode) {
    push(withModifiers(event, fromCode, true));
    if (!/[a-z]/i.test(fromCode)) push(withModifiers(event, fromCode, false));
  }

  return out;
}

/* ------------------------------ focus guards ------------------------------ */

export function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return target.closest('[contenteditable="true"], input, textarea, select') !== null;
}

/**
 * The rule that keeps typing an email from archiving it: while a text field has
 * focus, only chords carrying a real modifier (or an explicit opt-in) may fire.
 */
function firesWhileTyping(command: Command, chord: string): boolean {
  if (command.allowInEditable) return true;
  const step = chord.split(' ').pop() ?? chord;
  return step.startsWith('Mod+') || step.startsWith('Ctrl+') || step.startsWith('Alt+');
}

/* -------------------------------- dispatch -------------------------------- */

function activeRegistrations(): Registration[] {
  return registrations.filter((r) => r.enabled);
}

function mergedContext(): CommandContext {
  const merged: Partial<CommandContext> = {};
  // Outermost first so inner scopes override (e.g. the composer owns `send`).
  const ordered = [...activeRegistrations()].sort(
    (a, b) => SCOPE_PRECEDENCE.indexOf(b.scope) - SCOPE_PRECEDENCE.indexOf(a.scope) || a.order - b.order,
  );
  for (const reg of ordered) Object.assign(merged, reg.ref.current);
  // The app registers the complete context at 'global'; partial scopes only add.
  return merged as CommandContext;
}

function clearChord(): void {
  pendingChord = null;
  if (pendingTimer !== null) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }
}

function beginChord(prefix: string): void {
  clearChord();
  pendingChord = prefix;
  pendingTimer = setTimeout(clearChord, CHORD_TIMEOUT_MS);
}

function isChordPrefix(candidate: string, commands: Command[]): boolean {
  return commands.some((c) => c.run !== undefined && c.keys.some((k) => k.startsWith(`${candidate} `)));
}

function resolve(
  chords: string[],
  editable: boolean,
  commands: Command[],
): { command: Command; chord: string; ctx: CommandContext; run: (ctx: CommandContext) => void } | null {
  const scopes = new Set(activeRegistrations().map((r) => r.scope));
  const ctx = mergedContext();

  for (const scope of SCOPE_PRECEDENCE) {
    if (!scopes.has(scope)) continue;
    for (const command of commands) {
      if (command.scope !== scope) continue;
      // No `run` means another pane implements this key; leave the event alone.
      if (!command.run) continue;
      const chord = command.keys.find((k) => chords.includes(k));
      if (!chord) continue;
      if (editable && !firesWhileTyping(command, chord)) continue;
      if (command.when && !command.when(ctx)) continue;
      return { command, chord, ctx, run: command.run };
    }
  }
  return null;
}

function handleKeyDown(event: KeyboardEvent): void {
  // Mid-IME composition keystrokes belong to the input method, never to us.
  if (event.isComposing || event.keyCode === 229) return;
  if (event.defaultPrevented) return;
  if (registrations.length === 0) return;

  const commands = commandRegistry;
  const editable = isEditable(event.target);
  const chords = candidateKeys(event);
  if (chords.length === 0) return;

  if (pendingChord !== null) {
    const prefix = pendingChord;
    clearChord();
    const sequenced = chords.map((c) => `${prefix} ${c}`);
    const hit = resolve(sequenced, editable, commands);
    if (hit) {
      if (hit.command.preventDefault !== false) event.preventDefault();
      hit.run(hit.ctx);
    }
    return;
  }

  const hit = resolve(chords, editable, commands);
  if (hit) {
    if (hit.command.preventDefault !== false) event.preventDefault();
    hit.run(hit.ctx);
    return;
  }

  if (!editable) {
    const prefix = chords.find((c) => isChordPrefix(c, commands));
    if (prefix) {
      event.preventDefault();
      beginChord(prefix);
    }
  }
}

let commandRegistry: Command[] = defaultCommands;

/** Swap the dispatch table (tests, or a future user-remapping surface). */
export function setCommandRegistry(next: Command[]): void {
  commandRegistry = next;
}

/**
 * Capture phase on purpose. The thread list runs its own bubble-phase listener,
 * and a pending `g` chord would otherwise lose its second key to it — `g e`
 * would archive instead of going to Done. We only ever call preventDefault for
 * a key we actually own, so every other key reaches the pane untouched.
 */
function ensureListening(): void {
  if (listening || typeof window === 'undefined') return;
  window.addEventListener('keydown', handleKeyDown, true);
  window.addEventListener('blur', clearChord);
  listening = true;
}

function stopListeningIfIdle(): void {
  if (!listening || registrations.length > 0 || typeof window === 'undefined') return;
  window.removeEventListener('keydown', handleKeyDown, true);
  window.removeEventListener('blur', clearChord);
  listening = false;
  clearChord();
}

/* ---------------------------------- hook ---------------------------------- */

export type UseKeyboardOptions = { enabled?: boolean };

/**
 * The app registers the full context once at 'global'; every other scope
 * contributes only the handlers it owns.
 */
export function useKeyboard(scope: 'global', ctx: CommandContext, options?: UseKeyboardOptions): void;
export function useKeyboard(
  scope: Exclude<CommandScope, 'global'>,
  ctx: Partial<CommandContext>,
  options?: UseKeyboardOptions,
): void;
export function useKeyboard(
  scope: CommandScope,
  ctx: Partial<CommandContext>,
  options: UseKeyboardOptions = {},
): void {
  const enabled = options.enabled !== false;
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  const registrationRef = useRef<Registration | null>(null);
  if (registrationRef.current === null) {
    registrationRef.current = { scope, ref: ctxRef, enabled, order: orderCounter++ };
  }
  registrationRef.current.scope = scope;
  registrationRef.current.enabled = enabled;

  useEffect(() => {
    const registration = registrationRef.current;
    if (!registration) return;
    registrations.push(registration);
    ensureListening();
    return () => {
      const index = registrations.indexOf(registration);
      if (index >= 0) registrations.splice(index, 1);
      stopListeningIfIdle();
    };
  }, []);
}

/** True while a `g`-chord is waiting for its second key. */
export function chordPending(): string | null {
  return pendingChord;
}
